import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2, Mail, Phone, User, Clock, Tag, Activity, ShoppingCart } from 'lucide-react';
import { Button } from '../../components/ui/button';
import api from '../../lib/api';
import { toast } from 'sonner';

const AdminContact360 = () => {
  const navigate = useNavigate();
  const { contactId } = useParams();
  const [loading, setLoading] = useState(true);
  const [payload, setPayload] = useState(null);

  useEffect(() => {
    const fetchContact = async () => {
      setLoading(true);
      try {
        const res = await api.get(`/crm/contacts/${contactId}/360`);
        setPayload(res.data);
      } catch (error) {
        toast.error('No se pudo cargar la ficha 360');
      } finally {
        setLoading(false);
      }
    };
    fetchContact();
  }, [contactId]);

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('es-ES');
  };

  if (loading) {
    return (
      <div className="p-6 lg:p-8 min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  const contact = payload?.contact || {};
  const timeline = payload?.timeline || [];
  const tags = payload?.tags || [];
  const purchases = payload?.purchases || [];
  const audit = payload?.audit || [];

  return (
    <div className="p-6 lg:p-8 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="outline" size="icon" onClick={() => navigate('/admin/marketing')}>
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-semibold text-slate-900">Ficha Contacto 360</h1>
            <p className="text-slate-500 text-sm">{contact.email || '-'}</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl p-4 border border-slate-100 shadow-sm">
          <p className="text-xs uppercase text-slate-400 mb-2">Identidad</p>
          <div className="space-y-2 text-sm">
            <p className="flex items-center gap-2"><User className="w-4 h-4 text-slate-400" />{contact.first_name || '-'} {contact.last_name || ''}</p>
            <p className="flex items-center gap-2"><Mail className="w-4 h-4 text-slate-400" />{contact.email || '-'}</p>
            <p className="flex items-center gap-2"><Phone className="w-4 h-4 text-slate-400" />{contact.phone || contact.whatsapp || '-'}</p>
          </div>
        </div>

        <div className="bg-white rounded-xl p-4 border border-slate-100 shadow-sm">
          <p className="text-xs uppercase text-slate-400 mb-2">Lifecycle</p>
          <div className="space-y-2 text-sm">
            <p><span className="text-slate-500">Estado:</span> <span className="font-medium">{contact.status || '-'}</span></p>
            <p><span className="text-slate-500">Etapa:</span> <span className="font-medium">{contact.lifecycle_stage || '-'}</span></p>
            <p><span className="text-slate-500">Score:</span> <span className="font-medium">{contact.lead_score || 0}</span></p>
            <p><span className="text-slate-500">Temperatura:</span> <span className="font-medium">{contact.lead_temperature || '-'}</span></p>
          </div>
        </div>

        <div className="bg-white rounded-xl p-4 border border-slate-100 shadow-sm">
          <p className="text-xs uppercase text-slate-400 mb-2">Origen</p>
          <div className="space-y-2 text-sm">
            <p><span className="text-slate-500">Fuente:</span> <span className="font-medium">{contact.source || '-'}</span></p>
            <p><span className="text-slate-500">Detalle:</span> <span className="font-medium">{contact.source_detail || '-'}</span></p>
            <p className="flex items-center gap-2"><Clock className="w-4 h-4 text-slate-400" />{formatDateTime(contact.first_touch_at || contact.created_at)}</p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl p-4 border border-slate-100 shadow-sm">
        <p className="text-sm font-medium text-slate-900 mb-3 flex items-center gap-2">
          <Tag className="w-4 h-4" /> Tags
        </p>
        <div className="flex flex-wrap gap-2">
          {tags.map((t) => (
            <span key={t.id} className="px-2 py-1 text-xs rounded bg-slate-100 text-slate-700">{t.name}</span>
          ))}
          {tags.length === 0 && <span className="text-sm text-slate-400">Sin tags</span>}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl p-4 border border-slate-100 shadow-sm">
          <p className="text-sm font-medium text-slate-900 mb-3 flex items-center gap-2">
            <Activity className="w-4 h-4" /> Timeline
          </p>
          <div className="space-y-2 max-h-[420px] overflow-y-auto">
            {timeline.slice(0, 80).map((e) => (
              <div key={e.id || `${e.timestamp}-${e.event_name}`} className="p-3 bg-slate-50 rounded-lg">
                <p className="text-sm font-medium text-slate-800">{e.event_name || e.event_type || 'evento'}</p>
                <p className="text-xs text-slate-500 mt-1">{formatDateTime(e.timestamp || e.event_time)}</p>
                {e.source && <p className="text-xs text-slate-500 mt-1">source: {e.source}</p>}
              </div>
            ))}
            {timeline.length === 0 && <p className="text-sm text-slate-400">Sin eventos</p>}
          </div>
        </div>

        <div className="space-y-4">
          <div className="bg-white rounded-xl p-4 border border-slate-100 shadow-sm">
            <p className="text-sm font-medium text-slate-900 mb-3 flex items-center gap-2">
              <ShoppingCart className="w-4 h-4" /> Compras
            </p>
            <div className="space-y-2 max-h-[180px] overflow-y-auto">
              {purchases.map((p) => (
                <div key={p.id || `${p.created_at}-${p.amount}`} className="p-3 bg-emerald-50 rounded-lg">
                  <p className="text-sm font-medium text-slate-800">{p.product_name || p.product_slug || p.product_type || '-'}</p>
                  <p className="text-xs text-slate-500 mt-1">{formatDateTime(p.created_at)} · {p.status || '-'}</p>
                  <p className="text-sm font-semibold text-emerald-700 mt-1">{Number(p.amount || 0).toFixed(2)} €</p>
                </div>
              ))}
              {purchases.length === 0 && <p className="text-sm text-slate-400">Sin compras registradas</p>}
            </div>
          </div>

          <div className="bg-white rounded-xl p-4 border border-slate-100 shadow-sm">
            <p className="text-sm font-medium text-slate-900 mb-3">Solicitudes e intereses</p>
            <pre className="text-xs text-slate-600 bg-slate-50 rounded-lg p-3 overflow-x-auto">
{JSON.stringify({
  interests: contact.interests || {},
  requests: contact.requests || {},
  first_utm: contact.first_utm || {},
  last_utm: contact.last_utm || {}
}, null, 2)}
            </pre>
          </div>

          <div className="bg-white rounded-xl p-4 border border-slate-100 shadow-sm">
            <p className="text-sm font-medium text-slate-900 mb-3">Auditoría</p>
            <div className="space-y-2 max-h-[160px] overflow-y-auto">
              {audit.map((a) => (
                <div key={a.id || `${a.timestamp}-${a.action}`} className="text-xs bg-slate-50 rounded p-2">
                  <p className="font-medium text-slate-700">{a.action || '-'}</p>
                  <p className="text-slate-500">{formatDateTime(a.timestamp)}</p>
                </div>
              ))}
              {audit.length === 0 && <p className="text-sm text-slate-400">Sin cambios auditados</p>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminContact360;
